import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import costService from '../services/costService';
import categoriesService from '../services/categoriesService';
import { userService } from '../services/api';
import toast from 'react-hot-toast';

const RecurringExpenseForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    expense_category: 'company',
    category_id: '',
    is_recurring: true,
    recurrence_type: 'monthly',
    recurrence_day: new Date().getDate(),
    next_due_date: '',
    recurrence_end_date: '',
    responsible_person: 'EMPRESA',
    employee_ids: [],
    observations: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const { data: categoriesData } = useQuery({
    queryKey: ['expense-categories'],
    queryFn: categoriesService.getAll
  });

  const { data: employeesData } = useQuery({
    queryKey: ['employees-active'],
    queryFn: () => userService.getUsers({ limit: 200, active: true }),
    enabled: formData.expense_category === 'team'
  });

  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const formatted = tomorrow.toISOString().split('T')[0];
    if (!formData.next_due_date) {
      setFormData(prev => ({ ...prev, next_due_date: formatted }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleEmployeeToggle = (employeeId) => {
    setFormData(prev => {
      const ids = prev.employee_ids.includes(employeeId)
        ? prev.employee_ids.filter(id => id !== employeeId)
        : [...prev.employee_ids, employeeId];
      return { ...prev, employee_ids: ids };
    });
    if (errors.employee_ids) {
      setErrors(prev => ({ ...prev, employee_ids: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.description.trim()) {
      newErrors.description = 'Descrição é obrigatória';
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Valor deve ser maior que zero';
    }
    if (!formData.category_id) {
      newErrors.category_id = 'Categoria é obrigatória';
    }

    if (formData.is_recurring) {
      if (!formData.next_due_date) {
        newErrors.next_due_date = 'Data de vencimento é obrigatória';
      }
      if (!formData.recurrence_type || formData.recurrence_type === 'none') {
        newErrors.recurrence_type = 'Tipo de recorrência é obrigatório';
      }
    }

    if (formData.expense_category === 'team') {
      if (!formData.responsible_person.trim()) {
        newErrors.responsible_person = 'Responsável é obrigatório';
      }
      if (!formData.employee_ids || formData.employee_ids.length === 0) {
        newErrors.employee_ids = 'Selecione pelo menos um funcionário';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = () => {
    const payload = {
      description: formData.description.trim(),
      amount: parseFloat(formData.amount),
      expense_category: formData.expense_category,
      category_id: formData.category_id ? parseInt(formData.category_id) : null,
      observations: formData.observations?.trim() || null
    };

    if (formData.expense_category === 'team') {
      payload.responsible_person = formData.responsible_person.trim();
      payload.employee_ids = formData.employee_ids;
    }

    if (formData.is_recurring) {
      payload.recurrence_type = formData.recurrence_type;
      payload.recurrence_day = formData.recurrence_day || null;
      payload.next_due_date = formData.next_due_date;
      payload.recurrence_end_date = formData.recurrence_end_date || null;
    } else {
      payload.recurrence_type = 'none';
    }

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const payload = buildPayload();
      await costService.createRecurringCost(payload);
      toast.success('Despesa recorrente criada com sucesso!');
      navigate('/financeiro/saidas-previstas');
    } catch (error) {
      toast.error(error?.message || 'Erro ao criar despesa');
    } finally {
      setIsSubmitting(false);
    }
  };

  const categories = categoriesData?.data || [];
  const employees = employeesData?.data?.users || [];

  return (
    <div className="p-6">
      <div className="bg-white p-6 rounded-lg shadow mb-6 border-t-4 border-indigo-500">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              💰 Lançamento de Despesa Recorrente
            </h2>
            <p className="text-gray-600 text-sm mt-1">
              Cadastre despesas fixas como Aluguel, Luz, Água, etc.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Despesa
                </label>
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="is_recurring"
                      checked={!formData.is_recurring}
                      onChange={() => setFormData(prev => ({ ...prev, is_recurring: false }))}
                      className="w-4 h-4 text-indigo-600"
                    />
                    <span className="text-sm text-gray-700">Avulsa</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="is_recurring"
                      checked={formData.is_recurring}
                      onChange={() => setFormData(prev => ({ ...prev, is_recurring: true }))}
                      className="w-4 h-4 text-indigo-600"
                    />
                    <span className="text-sm text-gray-700 font-medium">Recorrente</span>
                  </label>
                </div>
              </div>

              {formData.is_recurring && (
                <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100 space-y-4">
                  <h3 className="font-medium text-indigo-900 text-sm">Configurações de Recorrência</h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tipo de Recorrência *
                      </label>
                      <select
                        name="recurrence_type"
                        value={formData.recurrence_type}
                        onChange={handleChange}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="daily">Diária</option>
                        <option value="weekly">Semanal</option>
                        <option value="monthly">Mensal</option>
                        <option value="yearly">Anual</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Dia do Vencimento
                      </label>
                      <input
                        type="number"
                        name="recurrence_day"
                        min="1"
                        max="31"
                        value={formData.recurrence_day}
                        onChange={handleChange}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        placeholder="1-31"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Primeiro Vencimento *
                      </label>
                      <input
                        type="date"
                        name="next_due_date"
                        value={formData.next_due_date}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                          errors.next_due_date ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.next_due_date && (
                        <p className="text-red-500 text-xs mt-1">{errors.next_due_date}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fim da Recorrência
                      </label>
                      <input
                        type="date"
                        name="recurrence_end_date"
                        value={formData.recurrence_end_date}
                        onChange={handleChange}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        placeholder="Opcional"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="p-4 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categoria de Despesa
                </label>
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="expense_category"
                      value="company"
                      checked={formData.expense_category === 'company'}
                      onChange={handleChange}
                      className="w-4 h-4 text-indigo-600"
                    />
                    <span className="text-sm text-gray-700">🏢 Empresa</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="expense_category"
                      value="team"
                      checked={formData.expense_category === 'team'}
                      onChange={handleChange}
                      className="w-4 h-4 text-indigo-600"
                    />
                    <span className="text-sm text-gray-700">👥 Equipe</span>
                  </label>
                </div>
              </div>

              {formData.expense_category === 'team' && (
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-100 space-y-4">
                  <h3 className="font-medium text-orange-900 text-sm">Dados da Equipe</h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Responsável *
                    </label>
                    <input
                      type="text"
                      name="responsible_person"
                      value={formData.responsible_person}
                      onChange={handleChange}
                      placeholder="Nome do responsável"
                      className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                        errors.responsible_person ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.responsible_person && (
                      <p className="text-red-500 text-xs mt-1">{errors.responsible_person}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Funcionários *
                    </label>
                    <div className="border border-gray-300 rounded-lg p-2 max-h-40 overflow-y-auto bg-white">
                      {employees.length === 0 ? (
                        <p className="text-gray-500 text-sm p-2">Carregando funcionários...</p>
                      ) : (
                        employees.map(emp => (
                          <label key={emp.id} className="flex items-center gap-2 p-1 hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.employee_ids.includes(emp.id)}
                              onChange={() => handleEmployeeToggle(emp.id)}
                              className="w-4 h-4 text-indigo-600 rounded"
                            />
                            <span className="text-sm text-gray-700">{emp.full_name}</span>
                          </label>
                        ))
                      )}
                    </div>
                    {errors.employee_ids && (
                      <p className="text-red-500 text-xs mt-1">{errors.employee_ids}</p>
                    )}
                    {formData.employee_ids.length > 0 && (
                      <p className="text-green-600 text-xs mt-1">
                        {formData.employee_ids.length} funcionário(s) selecionado(s)
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição *
                </label>
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Ex: Aluguel Escritório"
                  className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.description && (
                  <p className="text-red-500 text-xs mt-1">{errors.description}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor (R$) *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">R$</span>
                  <input
                    type="number"
                    name="amount"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={handleChange}
                    placeholder="0,00"
                    className={`w-full p-2 pl-10 border rounded-lg focus:ring-2 focus:ring-indigo-500 font-bold text-lg ${
                      errors.amount ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                </div>
                {errors.amount && (
                  <p className="text-red-500 text-xs mt-1">{errors.amount}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoria *
                </label>
                <select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleChange}
                  className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                    errors.category_id ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Selecione...</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                {errors.category_id && (
                  <p className="text-red-500 text-xs mt-1">{errors.category_id}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  name="observations"
                  rows="3"
                  value={formData.observations}
                  onChange={handleChange}
                  placeholder="Informações adicionais..."
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 shadow-sm flex items-center gap-2 disabled:opacity-50"
            >
              {isSubmitting ? (
                <>⏳ Salvando...</>
              ) : (
                <>💾 Lançar Despesa</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecurringExpenseForm;